<template>
  <div class="modal show">
    <!--Header-->
    <ModalHeader :title="title" @closeModal="closeModal"></ModalHeader>

    <!--Modal Body-->
    <div class="modal-body">
      <!--Notification Message-->
      <NotificationMessage
        :show="status.show"
        :status="status.status"
        :title="status.title"
        @closeModal="closeModal"
      ></NotificationMessage>

      <!--Available Zendesk Agents-->
      <div
        v-if="zendeskAgents.length > 0"
        class="grid-table-wrapper users-wrapper"
      >
        <!--Block Header-->
        <div class="block-header">
          <!--Search-->
          <div class="search-wrapper" @keyup="findAgents">
            <unicon name="search"></unicon>
            <FormBuilder
              :fields="zendeskFields"
              class="search-field"
            ></FormBuilder>
          </div>

          <!--License Count-->
          <div class="license-count">
            <span class="licenses-used">{{ assignedLicenses.length }}</span>
            <em>of</em>
            <span class="total-licenses">{{
              subscription.licenses.quantity
            }}</span>
            <em>licenses assigned</em>
          </div>

          <!--Primary Button-->
          <button class="btn btn-primary btn-small" @click="updateLicenses">
            Update Licenses
          </button>
        </div>

        <!--AvailanAgents-->
        <ul
          class="grid-table grid-table-body"
          ref="table"
          @scroll="tableScroll"
        >
          <li
            v-for="(user, index) in zendeskAgents"
            :key="index"
            class="grid-row row-select"
            :class="{ show: user.show }"
            @click="toggleAgent(user)"
          >
            <div class="grid-column check-column">
              <span class="check" :class="{ selected: user.selected }"></span>
              <div>
                <h3 class="name">{{ user.name }}</h3>
                <span class="email">{{ user.email }}</span>
              </div>
            </div>

            <div class="grid-column">
              <span :class="`table-tag ${getTagColor(user.role)}`">{{
                user.role
              }}</span>
            </div>
          </li>
        </ul>

        <!--Agent Count-->
        <span class="grid-count"
          >{{ zendeskAgents.length }}
          {{ zendeskAgents.length == 1 ? "agent" : "agents" }}</span
        >
      </div>

      <!--Pre Loader-->
      <PreLoader v-else :show="true"></PreLoader>
    </div>
  </div>
</template>
      
<script>
// Libraries
import _ from "lodash";

// Components
import ModalHeader from "@/components/modals/ModalHeader";
import FormBuilder from "@/components/form/FormBuilder";
import PreLoader from "@/components/loaders/PreLoader";
import NotificationMessage from "@/components/modals/NotificationMessage";

// Importing Services
import { SubscriptionService } from "@/services";

export default {
  name: "Licenses",
  props: {},
  data() {
    return {
      lodash: _,
      showOptions: {
        headerShadow: false,
      },
      title: "Manage Licenses",
      zendeskFields: {
        search: {
          type: "text",
          placeholder: "Search by name or email",
          required: false,
          hasError: false,
          additionalClass: "small-input",
          value: "",
        },
      },
      zendeskAgents: [],
      status: {
        show: false,
        status: "success",
        title: "",
      },
    };
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
    subscription() {
      return this.selectedApp.subscription;
    },
    activeAgents() {
      return this.subscription?.licenses?.users || [];
    },
    assignedLicenses() {
      return this.zendeskAgents.filter((agent) => agent.selected);
    },
  },
  components: {
    ModalHeader,
    FormBuilder,
    PreLoader,
    NotificationMessage,
  },
  created() {
    this.getZendeskAgents();
  },
  methods: {
    // Get Zendesk Agents
    async getZendeskAgents() {
      const response = await SubscriptionService.GetZendeskAgents();

      if (!response.hasError) {
        this.populateZendeskAgents(response.data);
      }
    },

    // Update the licensed users
    updateLicenses() {
      this.$emit("updateLicenses", this.assignedLicenses);
    },

    // Filter the agents by status
    populateZendeskAgents(agents) {
      const selectedIds = new Set(
        (this.activeAgents || []).map((a) => a.zendeskUserId)
      );

      this.zendeskAgents = _.sortBy(
        (agents || []).map((agent) => ({
          ...agent,
          zendeskUserId: agent.id.toString(),
          show: true,
          selected: selectedIds.has(agent.id.toString()),
        })),
        (a) => _.toLower(a?.name || a?.alias || a?.email || "")
      );
    },

    // Find Zendesk agents based on search
    findAgents() {
      const query = this.zendeskFields.search.value.trim().toLowerCase();

      this.zendeskAgents.forEach((agent) => {
        const name = (agent.name || "").toLowerCase();
        const email = (agent.email || "").toLowerCase();

        agent.show = !query || name.includes(query) || email.includes(query);
      });
    },

    // Toggle license status for agent
    toggleAgent(agent) {
      const currentStatus = agent.selected;

      if (currentStatus) {
        agent.selected = false;
      } else if (
        this.assignedLicenses.length < this.subscription.licenses.quantity
      ) {
        agent.selected = true;
      } else {
        this.status.title = `All ${this.subscription.licenses.quantity} licenses assigned`;
        this.showStatusMessage(this.status, 2500);
      }
    },

    // Event on table scroll
    tableScroll() {
      const scrollTop = this.$refs.table.scrollTop;

      // Check if header needs shadow
      if (scrollTop > 0) this.showOptions.headerShadow = true;
      else this.showOptions.headerShadow = false;
    },

    // Tag color based on role
    getTagColor(role) {
      switch (role) {
        case "admin":
          return "zendesk";
        case "agent":
          return "";
        default:
          break;
      }
    },

    closeModal() {
      this.$emit("closeModal");
    },
  },
};
</script>
      
<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.modal {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  top: 2rem;
  max-height: 30rem;
  min-width: 60vw;

  .modal-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: none;
  }
}

// Bound the flex height chain so the agent list (.grid-table-body, which
// already has overflow-y: auto from the shared dashboard styles) scrolls
// instead of overflowing and getting clipped by the modal.
.users-wrapper {
  flex: 1 1 auto;
  min-height: 0;
}

.grid-table-body {
  min-height: 0;
}

.block-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-bottom: 0.5rem;

  .license-count {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: flex-end;
    margin-right: 1rem;

    span {
      color: $greyColor;
      font-size: $normalFontSize;
      font-weight: $boldFontWeight;
      line-height: 1rem;
      margin-right: 0.25rem;
    }

    em {
      font-size: $smallFontSize;
      color: lighten($greyColor, 10%);
      line-height: 1rem;
      margin-right: 0.25rem;
    }
  }
}

.grid-row {
  display: none;

  &.show {
    display: flex;
  }
}

.grid-column {
  &:last-child {
    text-align: right;
  }
}

.name {
  font-size: $smallFontSize;
  font-weight: $mediumFontWeight;
}

.email {
  font-size: $smallerFontSize;
  color: $greyColor;
}

.grid-count {
  margin-bottom: 0;
}

.license-count {
  display: flex;
  flex-direction: row;
  align-items: flex-end;

  span {
    color: $purpleColor;
    font-size: $largestFontSize;
    font-weight: $boldFontWeight;
    line-height: 1rem;
    margin-right: 0.25rem;
  }

  em {
    color: $greyColor;
    line-height: 1rem;
    margin-right: 0.25rem;
  }
}

.search-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;

  .search-field {
    flex: 1;
  }

  .unicon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 1ch;
    z-index: 2;

    ::v-deep svg {
      fill: $greyColor;
      opacity: $lightOpacity;
      width: 1rem;
    }
  }

  ::v-deep {
    .field-item {
      margin-bottom: 0;
    }
    input {
      width: calc(100% - 3.15rem - 2px);
      padding: 0.75rem 1.15rem 0.75rem 2rem;
    }
  }
}

.check-column {
  display: flex;
  flex-direction: row;
}

.check {
  display: block;
  background: darken($whiteColor, 10%);
  border: 2px solid darken($whiteColor, 10.5%);
  height: 1rem;
  width: 1rem;
  border-radius: 0.35rem;
  margin-right: 2rem;

  &.selected {
    background: $greenColor;
    border: 2px solid lighten($greenColor, 10.5%);
  }
}
</style>